/**
 * Share Helpers - Utility functions for chart sharing
 */

import { devWarn } from './devLog';
import { ChartMetadata, ShareConfig } from '../types/share.types';

/**
 * Parse JatakaResponse basic_info date + time to ISO 8601
 *
 * @param date - Format: "August 08, 2025"
 * @param time - Format: "00:42:57"
 * @returns ISO 8601 string without timezone: "2025-08-08T00:42:57"
 */
export function parseDateTimeFromBasicInfo(date: string, time: string): string {
  // Parse "August 08, 2025" format
  const parsedDate = new Date(date);

  if (isNaN(parsedDate.getTime())) {
    throw new Error(`Invalid date format: ${date}`);
  }

  // Parse time "00:42:57"
  const [hours, minutes, seconds] = time.split(':').map(Number);

  if (hours === undefined || minutes === undefined || seconds === undefined) {
    throw new Error(`Invalid time format: ${time}`);
  }

  // Format to ISO 8601 WITHOUT timezone - use local time components directly
  // DO NOT use toISOString() as it converts to UTC!
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  const hoursStr = String(hours).padStart(2, '0');
  const minutesStr = String(minutes).padStart(2, '0');
  const secondsStr = String(seconds).padStart(2, '0');

  return `${year}-${month}-${day}T${hoursStr}:${minutesStr}:${secondsStr}`;
}

/**
 * Convert camelCase to snake_case
 *
 * @param str - String in camelCase
 * @returns String in snake_case
 */
export function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

/**
 * Convert object keys from camelCase to snake_case
 *
 * @param obj - Object with camelCase keys
 * @returns New object with snake_case keys
 */
export function keysToSnakeCase<T extends Record<string, any>>(obj: T): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [toSnakeCase(key), value])
  );
}

/**
 * Section mapping: config keys → response keys
 */
export const SECTION_MAPPING = {
  planet_positions: 'graha_positions',
  panchanga: 'panchanga',
  detailed_panchanga: 'detailed_panchanga',
  astronomical_data: 'astronomical',
} as const;

/**
 * Check if section should be shown based on visible sections list
 *
 * @param configKey - Section key from share_config.sections
 * @param visibleSections - Array of visible section keys from response
 * @returns true if section should be displayed
 */
export function shouldShowSection(
  configKey: keyof typeof SECTION_MAPPING,
  visibleSections: string[]
): boolean {
  const responseKey = SECTION_MAPPING[configKey];
  return visibleSections.includes(responseKey);
}

/**
 * Get response key for config key
 *
 * @param configKey - Section key from config
 * @returns Corresponding response key
 */
export function getResponseKey(configKey: string): string {
  return SECTION_MAPPING[configKey as keyof typeof SECTION_MAPPING] || configKey;
}

/**
 * Build ChartMetadata from JatakaResponse
 * Extracts all required fields for backend compatibility
 * Updated 2025-10-20: Uses flat structure for coordinates
 *
 * @param jatakaResponse - Complete JatakaResponse from API
 * @param displayName - Optional name to include (can be hidden in share)
 * @returns ChartMetadata ready for backend
 */
export function buildChartMetadataFromResponse(
  jatakaResponse: any,
  displayName?: string
): ChartMetadata {
  const basicInfo = jatakaResponse.basic_info;
  const calculationMeta = jatakaResponse.calculation_metadata;

  // Parse datetime from basic_info (date + time)
  const birthTime = parseDateTimeFromBasicInfo(
    basicInfo.date,      // "August 08, 2025"
    basicInfo.time       // "00:42:57"
  );

  // Extract timezone_offset (with fallback parsing)
  let timezoneOffset = calculationMeta?.timezone_offset;

  if (timezoneOffset === undefined) {
    const timezone = basicInfo.timezone || '';

    // Handle UTC explicitly
    if (timezone === 'UTC' || timezone === '0:00:00' || timezone.startsWith('0:00:00')) {
      timezoneOffset = 0;
    } else {
      // Try format "UTC+02:00" or "UTC-05:30"
      const utcMatch = timezone.match(/^UTC([+-])(\d{2}):(\d{2})$/);
      if (utcMatch) {
        const sign = utcMatch[1] === '+' ? 1 : -1;
        const hours = parseInt(utcMatch[2], 10);
        const minutes = parseInt(utcMatch[3], 10);
        timezoneOffset = sign * (hours + minutes / 60);
      } else {
        // Parse from timezone string: "3:00:00 (East of GMT)" or "-5:00:00 (West of GMT)"
        const timezoneMatch = timezone.match(/^(-?\d+):(\d+):(\d+)/);
        if (timezoneMatch) {
          const hours = parseInt(timezoneMatch[1], 10);
          const minutes = parseInt(timezoneMatch[2], 10);
          const seconds = parseInt(timezoneMatch[3], 10);
          timezoneOffset = hours + minutes / 60 + seconds / 3600;
        } else {
          // Fallback to 0 if can't parse
          devWarn(`Could not parse timezone "${timezone}", defaulting to UTC (0)`);
          timezoneOffset = 0;
        }
      }
    }
  }

  return {
    // Display metadata
    name: displayName,
    birth_time: birthTime,  // Local time without Z (backend expects naive datetime)
    birth_place: basicInfo.place_name || 'Unknown',
    timezone: basicInfo.timezone || 'UTC',

    // Calculation parameters (flat structure!)
    latitude: basicInfo.latitudeValue,
    longitude: basicInfo.longitudeValue,
    timezone_offset: timezoneOffset,
    altitude: parseFloat(basicInfo.altitude) || 0.0,
  };
}

/**
 * Apply local filtering to chart metadata based on share_config
 * Used for guest view mode - filters data client-side without API call
 *
 * @param metadata - Original chart metadata (author view)
 * @param shareConfig - Share configuration with field visibility
 * @returns Filtered metadata with hidden fields replaced by placeholders
 */
export function applyShareConfigFilter(
  metadata: ChartMetadata,
  shareConfig: ShareConfig
): ChartMetadata {
  const filtered = { ...metadata };

  // Backend returns snake_case keys, frontend uses camelCase
  // Need to check both formats
  const fields: any = shareConfig.fields;

  // Apply field visibility filters
  if (!shareConfig.fields.name) {
    filtered.name = undefined;  // Hide name
  }

  // Check both birthTime (camelCase) and birth_time (snake_case)
  if (!(fields.birthTime ?? fields.birth_time ?? true)) {
    filtered.birth_time = '-';  // Placeholder for hidden time
  }

  // Check both birthPlace (camelCase) and birth_place (snake_case)
  if (!(fields.birthPlace ?? fields.birth_place ?? true)) {
    filtered.birth_place = '-';  // Placeholder for hidden place
  }

  if (!shareConfig.fields.coordinates) {
    filtered.latitude = 0;  // Placeholder for hidden coords
    filtered.longitude = 0;
  }

  if (!shareConfig.fields.timezone) {
    filtered.timezone = '-';  // Placeholder for hidden timezone
  }

  return filtered;
}
